import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BellRing, Check, CheckCircle2, Gauge, Layers, Megaphone, MessageSquare, PlusCircle, Shapes, Trash2, Trophy, UserRoundPen, Users2, Webhook } from "lucide-react";

import {
  listarTiposDeTarefa,
  criarTipoDeTarefa,
  atualizarTipoDeTarefa,
  removerTipoDeTarefa,
} from "../services/tipoDeTarefaService";
import {
  listarMotivosTarefa,
  criarMotivoTarefa,
  atualizarMotivoTarefa,
  removerMotivoTarefa,
} from "../services/motivoTarefaService";
import {
  listarConcorrentes,
  criarConcorrente,
  atualizarConcorrente,
  removerConcorrente,
} from "../services/concorrenteService";
import {
  listarAvisosParaGestao,
  criarAviso,
  atualizarAviso,
  enviarImagemAviso,
  excluirAviso,
} from "../services/avisoService";
import {
  atualizarPoliticaNotificacao,
  listarPoliticasNotificacao,
} from "../services/notificationService";
import {
  criarRegraWebhookJoin,
  atualizarRegraWebhookJoin,
  listarRegrasWebhookJoin,
  removerRegraWebhookJoin,
} from "../services/joinWebhookRuleService";
import { getUsuarioLogado } from "../services/LoginService";
import { formatIsoDateToDisplay } from "../utils/dateFilters";
import { listarTodosUsuariosAtivos } from "../services/usuarioService";
import { atualizarReguaChurn, buscarReguaChurn } from "../services/churnService";
import { Button, Card, Checkbox, DataTable, FormField, Modal, MultiSelectField, PageLoader, StatusBadge, Tabs, tabPanelProps } from "../components/ui";
import { SwalWithTheme } from "../utils/sweetalert";
import PreFiltrosSection from "../components/configuracoes/PreFiltrosSection";
import CampanhasSection from "../components/configuracoes/CampanhasSection";
import TextoRico from "../components/TextoRico";
import { resolveTaskTypeIconDefinition, TASK_TYPE_ICON_OPTIONS } from "../utils/taskTypeIcons";
import {
  APPROVAL_MODE_OPTIONS,
  CHECK_POLICY_OPTIONS,
  FIELD_MODE_OPTIONS,
  IMAGE_REQUIREMENT_OPTIONS,
  PARTNER_BINDING_OPTIONS,
  STEP_MODE_OPTIONS,
  TASK_FIELD_CODES,
  createDefaultMotivoFields,
  createDefaultTipoRules,
  describeCheckPolicy,
  describeFieldMode,
  describePartnerBinding,
  describeStepPolicy,
  hasVisibleField,
  normalizeMotivoFields,
  normalizeTipoRules,
} from "../utils/taskRules";

const calcProgress = (checks = []) => {
  const total = checks.length || 1;
  const done = checks.filter(Boolean).length;
  return Math.round((done / total) * 100);
};

const FIELD_LABELS = {
  [TASK_FIELD_CODES.BANCO_NEGOCIADO]: "Banco",
  [TASK_FIELD_CODES.PRODUTO_NEGOCIADO]: "Produto",
  [TASK_FIELD_CODES.CONVENIO_NEGOCIADO]: "Convenio",
  [TASK_FIELD_CODES.VALOR_PRODUZIDO]: "Valor",
  [TASK_FIELD_CODES.CONCORRENTE]: "Concorrente",
};

const NOTIFICATION_SEVERITY_OPTIONS = [
  { value: "INFO", label: "Informativo" },
  { value: "WARNING", label: "Atencao" },
  { value: "SUCCESS", label: "Sucesso" },
  { value: "CRITICAL", label: "Critico" },
];

// Uma aba por assunto, em vez de sete cartões empilhados. A aba ativa vai para ?aba= para o link
// voltar no mesmo lugar.
const SECOES = [
  { value: "tarefas", label: "Tarefas", icon: Shapes },
  { value: "join", label: "Automações Join", icon: Webhook },
  { value: "notificacoes", label: "Notificações", icon: BellRing },
  { value: "hub", label: "Avisos do hub", icon: Megaphone },
  { value: "churn", label: "Régua de churn", icon: Gauge },
  { value: "pre-filtros", label: "Pré-filtros", icon: Layers },
  { value: "campanhas", label: "Campanhas", icon: Trophy },
];

const POLICY_RECIPIENTS = [
  { field: "notifyAssignee", label: "Responsável", description: "Quem executa a tarefa" },
  { field: "notifyAdmins", label: "Administradores", description: "Time de administração" },
  { field: "notifyCreator", label: "Criador", description: "Quem abriu a tarefa" },
  { field: "notifySupervisors", label: "Supervisores", description: "Supervisores do responsável" },
];

const POLICY_EDITABLE_FIELDS = ["enabled", "severity", ...POLICY_RECIPIENTS.map((item) => item.field)];

const WEBHOOK_TASK_PRIORITY_OPTIONS = [
  { value: "Baixa", label: "Baixa" },
  { value: "Media", label: "Media" },
  { value: "Alta", label: "Alta" },
  { value: "Urgente", label: "Urgente" },
];

const createDefaultWebhookRuleForm = () => ({
  ativo: true,
  eventType: "",
  descricao: "",
  criarTarefa: true,
  tipoTarefaId: "",
  motivosIds: [],
  usuarioResponsavelId: "",
  prioridade: "",
  tituloTarefaTemplate: "",
  observacoesTarefaTemplate: "",
  gerarNotificacao: false,
  tituloNotificacaoTemplate: "",
  mensagemNotificacaoTemplate: "",
});

function createDefaultAvisoForm() {
  return {
    etiqueta: "",
    titulo: "",
    descricao: "",
    imagemUrl: "",
    linkDestino: "",
    acaoLabel: "",
    somenteImagem: false,
    ordem: 0,
    dataInicio: "",
    dataFim: "",
  };
}

function formatVigencia(aviso) {
  const inicio = aviso?.dataInicio ? formatIsoDateToDisplay(aviso.dataInicio) : "";
  const fim = aviso?.dataFim ? formatIsoDateToDisplay(aviso.dataFim) : "";
  if (!inicio && !fim) return "Sempre";
  if (inicio && fim) return `${inicio} a ${fim}`;
  return inicio ? `A partir de ${inicio}` : `Ate ${fim}`;
}

export default function ConfiguracoesAtividades() {
  const [usuarioLogado, setUsuarioLogado] = useState(null);
  const [loading, setLoading] = useState(true);

  const [tiposList, setTiposList] = useState([]);
  const [motivosList, setMotivosList] = useState([]);
  const [concorrentesList, setConcorrentesList] = useState([]);
  const [notificationPolicies, setNotificationPolicies] = useState([]);
  // Cópia do que está gravado, para saber quais políticas têm alteração pendente.
  const [savedPolicies, setSavedPolicies] = useState({});
  const [joinWebhookRules, setJoinWebhookRules] = useState([]);
  const [usuariosList, setUsuariosList] = useState([]);
  const [savingPolicyCode, setSavingPolicyCode] = useState(null);
  const [savingChurnRegua, setSavingChurnRegua] = useState(false);
  const [churnRegua, setChurnRegua] = useState({
    entregaAtencao: "",
    entregaRisco: "",
    entregaCritico: "",
  });

  const [showTipoModal, setShowTipoModal] = useState(false);
  const [editingTipoId, setEditingTipoId] = useState(null);
  const [tipoForm, setTipoForm] = useState({
    nomeTipo: "",
    icone: "",
    scoreTipo: 0,
    regras: createDefaultTipoRules(),
  });

  const [showMotivoModal, setShowMotivoModal] = useState(false);
  const [editingMotivoId, setEditingMotivoId] = useState(null);
  const [motivoForm, setMotivoForm] = useState({
    nome: "",
    campos: createDefaultMotivoFields(),
  });

  const [showConcorrenteModal, setShowConcorrenteModal] = useState(false);
  const [editingConcorrenteId, setEditingConcorrenteId] = useState(null);
  const [concorrenteForm, setConcorrenteForm] = useState({ nome: "" });

  // Avisos do carrossel da tela inicial (hub). A imagem sobe depois de salvar, porque o upload
  // precisa do id do aviso.
  const [avisosList, setAvisosList] = useState([]);
  const [showAvisoModal, setShowAvisoModal] = useState(false);
  const [editingAvisoId, setEditingAvisoId] = useState(null);
  const [avisoForm, setAvisoForm] = useState(createDefaultAvisoForm());
  const [avisoImagemFile, setAvisoImagemFile] = useState(null);

  const [showWebhookRuleModal, setShowWebhookRuleModal] = useState(false);
  const [editingWebhookRuleId, setEditingWebhookRuleId] = useState(null);
  const [webhookRuleForm, setWebhookRuleForm] = useState(createDefaultWebhookRuleForm());

  const [searchParams, setSearchParams] = useSearchParams();
  const abaAtiva = SECOES.some((secao) => secao.value === searchParams.get("aba")) ? searchParams.get("aba") : SECOES[0].value;
  const setAbaAtiva = (aba) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("aba", aba);
      return next;
    }, { replace: true });
  };

  const isAdmin = String(usuarioLogado?.cargo || "").trim().toLowerCase() === "admin";

  const carregarDados = async () => {
    setLoading(true);
    try {
      const user = await getUsuarioLogado();
      setUsuarioLogado(user);

      if (String(user?.cargo || "").trim().toLowerCase() !== "admin") {
        setTiposList([]);
        setMotivosList([]);
        setConcorrentesList([]);
        setNotificationPolicies([]);
        setJoinWebhookRules([]);
        setUsuariosList([]);
        return;
      }

      const [tiposResult, motivosResult, concorrentesResult, notificacoesResult, regrasWebhookResult, usuariosResult, avisosResult, churnReguaResult] = await Promise.allSettled([
        listarTiposDeTarefa(),
        listarMotivosTarefa(),
        listarConcorrentes(),
        listarPoliticasNotificacao(),
        listarRegrasWebhookJoin(),
        listarTodosUsuariosAtivos({ suppressActivityTracking: true }),
        listarAvisosParaGestao(),
        buscarReguaChurn(),
      ]);

      setTiposList(tiposResult.status === "fulfilled" ? tiposResult.value || [] : []);
      setMotivosList(motivosResult.status === "fulfilled" ? motivosResult.value || [] : []);
      setConcorrentesList(concorrentesResult.status === "fulfilled" ? concorrentesResult.value || [] : []);
      const policies = notificacoesResult.status === "fulfilled" ? notificacoesResult.value || [] : [];
      setNotificationPolicies(policies);
      setSavedPolicies(Object.fromEntries(policies.map((policy) => [policy.eventCode, policy])));
      setJoinWebhookRules(regrasWebhookResult.status === "fulfilled" ? regrasWebhookResult.value || [] : []);
      setUsuariosList(usuariosResult.status === "fulfilled" ? usuariosResult.value || [] : []);
      setAvisosList(avisosResult.status === "fulfilled" ? avisosResult.value || [] : []);
      if (churnReguaResult.status === "fulfilled") {
        setChurnRegua({
          entregaAtencao: churnReguaResult.value?.entregaAtencao ?? "",
          entregaRisco: churnReguaResult.value?.entregaRisco ?? "",
          entregaCritico: churnReguaResult.value?.entregaCritico ?? "",
        });
      }

      const falhas = [
        tiposResult.status === "rejected" ? "tipos de tarefa" : null,
        motivosResult.status === "rejected" ? "motivos" : null,
        concorrentesResult.status === "rejected" ? "concorrentes" : null,
        notificacoesResult.status === "rejected" ? "notificacoes" : null,
        regrasWebhookResult.status === "rejected" ? "automacoes webhook" : null,
        usuariosResult.status === "rejected" ? "usuarios" : null,
        avisosResult.status === "rejected" ? "avisos do hub" : null,
        churnReguaResult.status === "rejected" ? "regua de churn" : null,
      ].filter(Boolean);

      if (falhas.length > 0) {
        SwalWithTheme.error("Erro", `Erro ao carregar: ${falhas.join(", ")}`);
      }
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao carregar configuracoes";
      SwalWithTheme.error("Erro", message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarDados();
  }, []);

  const salvarReguaChurn = async () => {
    const entregaAtencao = Number(churnRegua.entregaAtencao);
    const entregaRisco = Number(churnRegua.entregaRisco);
    const entregaCritico = Number(churnRegua.entregaCritico);

    if ([entregaAtencao, entregaRisco, entregaCritico].some((valor) => !Number.isFinite(valor) || valor < 0 || valor > 100)) {
      SwalWithTheme.error("Erro", "Informe percentuais entre 0 e 100 para a regua de entrega.");
      return;
    }
    if (!(entregaAtencao > entregaRisco && entregaRisco > entregaCritico)) {
      SwalWithTheme.error("Erro", "Os limites precisam ser decrescentes: atencao maior que risco e risco maior que critico.");
      return;
    }

    setSavingChurnRegua(true);
    try {
      const atualizada = await atualizarReguaChurn({ entregaAtencao, entregaRisco, entregaCritico });
      setChurnRegua({
        entregaAtencao: atualizada.entregaAtencao ?? entregaAtencao,
        entregaRisco: atualizada.entregaRisco ?? entregaRisco,
        entregaCritico: atualizada.entregaCritico ?? entregaCritico,
      });
      SwalWithTheme.success("Sucesso", "Regua de churn atualizada.");
    } catch (error) {
      const message = error?.response?.data?.message || error?.response?.data || "Erro ao atualizar a regua de churn";
      SwalWithTheme.error("Erro", String(message));
    } finally {
      setSavingChurnRegua(false);
    }
  };

  const openTipoModal = (tipo = null) => {
    if (tipo) {
      setEditingTipoId(tipo.id);
      setTipoForm({
        nomeTipo: tipo.nomeTipo || "",
        icone: tipo.icone || "",
        scoreTipo: Number(tipo.scoreTipo || 0),
        regras: normalizeTipoRules(tipo.regras),
      });
    } else {
      setEditingTipoId(null);
      setTipoForm({
        nomeTipo: "",
        icone: "",
        scoreTipo: 0,
        regras: createDefaultTipoRules(),
      });
    }
    setShowTipoModal(true);
  };

  const openMotivoModal = (motivo = null) => {
    if (motivo) {
      setEditingMotivoId(motivo.id);
      setMotivoForm({
        nome: motivo.nome || "",
        campos: normalizeMotivoFields(motivo.campos),
      });
    } else {
      setEditingMotivoId(null);
      setMotivoForm({
        nome: "",
        campos: createDefaultMotivoFields(),
      });
    }
    setShowMotivoModal(true);
  };

  const openConcorrenteModal = (concorrente = null) => {
    if (concorrente) {
      setEditingConcorrenteId(concorrente.id);
      setConcorrenteForm({ nome: concorrente.nome || "" });
    } else {
      setEditingConcorrenteId(null);
      setConcorrenteForm({ nome: "" });
    }
    setShowConcorrenteModal(true);
  };

  const openAvisoModal = (aviso = null) => {
    if (aviso) {
      setEditingAvisoId(aviso.id);
      setAvisoForm({
        etiqueta: aviso.etiqueta || "",
        titulo: aviso.titulo || "",
        descricao: aviso.descricao || "",
        imagemUrl: aviso.imagemUrl || "",
        linkDestino: aviso.linkDestino || "",
        acaoLabel: aviso.acaoLabel || "",
        somenteImagem: Boolean(aviso.somenteImagem),
        ordem: Number(aviso.ordem) || 0,
        dataInicio: aviso.dataInicio || "",
        dataFim: aviso.dataFim || "",
      });
    } else {
      setEditingAvisoId(null);
      setAvisoForm(createDefaultAvisoForm());
    }
    setAvisoImagemFile(null);
    setShowAvisoModal(true);
  };

  const openWebhookRuleModal = (regra = null) => {
    if (regra) {
      setEditingWebhookRuleId(regra.id);
      setWebhookRuleForm({
        ativo: Boolean(regra.ativo),
        eventType: regra.eventType || "",
        descricao: regra.descricao || "",
        criarTarefa: Boolean(regra.criarTarefa),
        tipoTarefaId: regra.tipoTarefaId ? String(regra.tipoTarefaId) : "",
        motivosIds: Array.isArray(regra.motivosIds) ? regra.motivosIds : [],
        usuarioResponsavelId: regra.usuarioResponsavelId ? String(regra.usuarioResponsavelId) : "",
        prioridade: regra.prioridade || "",
        tituloTarefaTemplate: regra.tituloTarefaTemplate || "",
        observacoesTarefaTemplate: regra.observacoesTarefaTemplate || "",
        gerarNotificacao: Boolean(regra.gerarNotificacao),
        tituloNotificacaoTemplate: regra.tituloNotificacaoTemplate || "",
        mensagemNotificacaoTemplate: regra.mensagemNotificacaoTemplate || "",
      });
    } else {
      setEditingWebhookRuleId(null);
      setWebhookRuleForm(createDefaultWebhookRuleForm());
    }
    setShowWebhookRuleModal(true);
  };

  const updateTipoRules = (updater) => {
    setTipoForm((prev) => ({ ...prev, regras: updater(normalizeTipoRules(prev.regras)) }));
  };

  const updateMotivoField = (fieldCode, value) => {
    setMotivoForm((prev) => ({
      ...prev,
      campos: {
        ...normalizeMotivoFields(prev.campos),
        fields: {
          ...normalizeMotivoFields(prev.campos).fields,
          [fieldCode]: value,
        },
      },
    }));
  };

  const saveTipo = async () => {
    if (!tipoForm.nomeTipo?.trim()) {
      SwalWithTheme.error("Erro", "Nome do tipo e obrigatorio");
      return;
    }

    try {
      const payload = {
        nomeTipo: tipoForm.nomeTipo.trim(),
        icone: String(tipoForm.icone || "").trim() || null,
        scoreTipo: Number(tipoForm.scoreTipo ?? 0),
        regras: normalizeTipoRules(tipoForm.regras),
      };

      if (editingTipoId) {
        await atualizarTipoDeTarefa(editingTipoId, payload);
      } else {
        await criarTipoDeTarefa(payload);
      }
      setShowTipoModal(false);
      setTiposList(await listarTiposDeTarefa());
      SwalWithTheme.success("Sucesso", `Tipo ${editingTipoId ? "atualizado" : "criado"}`);
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao salvar tipo";
      SwalWithTheme.error("Erro", message);
    }
  };

  const saveMotivo = async () => {
    if (!motivoForm.nome?.trim()) {
      SwalWithTheme.error("Erro", "Nome do motivo e obrigatorio");
      return;
    }

    try {
      const payload = {
        nome: motivoForm.nome.trim(),
        campos: normalizeMotivoFields(motivoForm.campos),
      };
      if (editingMotivoId) {
        await atualizarMotivoTarefa(editingMotivoId, payload);
      } else {
        await criarMotivoTarefa(payload);
      }
      setShowMotivoModal(false);
      setMotivosList(await listarMotivosTarefa());
      SwalWithTheme.success("Sucesso", `Motivo ${editingMotivoId ? "atualizado" : "criado"}`);
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao salvar motivo";
      SwalWithTheme.error("Erro", message);
    }
  };

  const saveConcorrente = async () => {
    if (!concorrenteForm.nome?.trim()) {
      SwalWithTheme.error("Erro", "Nome do concorrente e obrigatorio");
      return;
    }

    try {
      if (editingConcorrenteId) {
        await atualizarConcorrente(editingConcorrenteId, concorrenteForm);
      } else {
        await criarConcorrente(concorrenteForm);
      }
      setShowConcorrenteModal(false);
      setConcorrentesList(await listarConcorrentes());
      SwalWithTheme.success("Sucesso", `Concorrente ${editingConcorrenteId ? "atualizado" : "criado"}`);
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao salvar concorrente";
      SwalWithTheme.error("Erro", message);
    }
  };

  const saveAviso = async () => {
    if (!avisoForm.titulo?.trim()) {
      SwalWithTheme.error("Erro", "Titulo do aviso e obrigatorio");
      return;
    }
    if (avisoForm.somenteImagem && !avisoImagemFile && !avisoForm.imagemUrl?.trim()) {
      SwalWithTheme.error("Erro", "Aviso somente imagem precisa de uma imagem");
      return;
    }
    if (avisoForm.dataInicio && avisoForm.dataFim && avisoForm.dataFim < avisoForm.dataInicio) {
      SwalWithTheme.error("Erro", "A data de fim deve ser igual ou posterior a data de inicio");
      return;
    }

    const payload = {
      ...avisoForm,
      ordem: Number(avisoForm.ordem) || 0,
      dataInicio: avisoForm.dataInicio || null,
      dataFim: avisoForm.dataFim || null,
    };

    try {
      // O upload precisa do id, então a imagem sobe depois de criar/atualizar o aviso.
      const salvo = editingAvisoId ? await atualizarAviso(editingAvisoId, payload) : await criarAviso(payload);
      if (avisoImagemFile) {
        await enviarImagemAviso(salvo.id, avisoImagemFile);
      }
      setShowAvisoModal(false);
      setAvisoImagemFile(null);
      setAvisosList(await listarAvisosParaGestao());
      SwalWithTheme.success("Sucesso", `Aviso ${editingAvisoId ? "atualizado" : "criado"}`);
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao salvar aviso";
      SwalWithTheme.error("Erro", message);
    }
  };

  const saveWebhookRule = async () => {
    if (!webhookRuleForm.eventType?.trim()) {
      SwalWithTheme.error("Erro", "eventType e obrigatorio");
      return;
    }

    if (!webhookRuleForm.criarTarefa && !webhookRuleForm.gerarNotificacao) {
      SwalWithTheme.error("Erro", "A regra precisa criar tarefa, notificacao ou ambos");
      return;
    }

    if (webhookRuleForm.criarTarefa && !webhookRuleForm.tipoTarefaId) {
      SwalWithTheme.error("Erro", "Selecione o tipo de tarefa da automacao");
      return;
    }

    try {
      const payload = {
        ativo: Boolean(webhookRuleForm.ativo),
        eventType: webhookRuleForm.eventType.trim(),
        descricao: String(webhookRuleForm.descricao || "").trim() || null,
        criarTarefa: Boolean(webhookRuleForm.criarTarefa),
        tipoTarefaId: webhookRuleForm.criarTarefa && webhookRuleForm.tipoTarefaId
          ? Number(webhookRuleForm.tipoTarefaId)
          : null,
        motivosIds: webhookRuleForm.criarTarefa
          ? Array.from(
            new Set(
              (Array.isArray(webhookRuleForm.motivosIds) ? webhookRuleForm.motivosIds : [])
                .map((id) => Number(id))
                .filter((id) => Number.isFinite(id) && id > 0)
            )
          )
          : [],
        usuarioResponsavelId: webhookRuleForm.criarTarefa && webhookRuleForm.usuarioResponsavelId
          ? Number(webhookRuleForm.usuarioResponsavelId)
          : null,
        prioridade: webhookRuleForm.criarTarefa
          ? String(webhookRuleForm.prioridade || "").trim() || null
          : null,
        tituloTarefaTemplate: webhookRuleForm.criarTarefa
          ? String(webhookRuleForm.tituloTarefaTemplate || "").trim() || null
          : null,
        observacoesTarefaTemplate: webhookRuleForm.criarTarefa
          ? String(webhookRuleForm.observacoesTarefaTemplate || "").trim() || null
          : null,
        gerarNotificacao: Boolean(webhookRuleForm.gerarNotificacao),
        tituloNotificacaoTemplate: webhookRuleForm.gerarNotificacao
          ? String(webhookRuleForm.tituloNotificacaoTemplate || "").trim() || null
          : null,
        mensagemNotificacaoTemplate: webhookRuleForm.gerarNotificacao
          ? String(webhookRuleForm.mensagemNotificacaoTemplate || "").trim() || null
          : null,
      };

      if (editingWebhookRuleId) {
        await atualizarRegraWebhookJoin(editingWebhookRuleId, payload);
      } else {
        await criarRegraWebhookJoin(payload);
      }

      setShowWebhookRuleModal(false);
      setJoinWebhookRules(await listarRegrasWebhookJoin());
      SwalWithTheme.success("Sucesso", `Automacao ${editingWebhookRuleId ? "atualizada" : "criada"}`);
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao salvar automacao do webhook";
      SwalWithTheme.error("Erro", message);
    }
  };

  const handleExcluirTipo = async (tipo) => {
    const result = await SwalWithTheme.confirm("Excluir tipo", `Deseja excluir o tipo \"${tipo.nomeTipo}\"?`, "Excluir", "Cancelar");
    if (!result.isConfirmed) return;

    try {
      await removerTipoDeTarefa(tipo.id);
      setTiposList(await listarTiposDeTarefa());
      SwalWithTheme.success("Sucesso", "Tipo removido");
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao remover tipo";
      SwalWithTheme.error("Erro", message);
    }
  };

  const handleExcluirMotivo = async (motivo) => {
    const result = await SwalWithTheme.confirm("Excluir motivo", `Deseja excluir o motivo \"${motivo.nome}\"?`, "Excluir", "Cancelar");
    if (!result.isConfirmed) return;

    try {
      await removerMotivoTarefa(motivo.id);
      setMotivosList(await listarMotivosTarefa());
      SwalWithTheme.success("Sucesso", "Motivo removido");
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao remover motivo";
      SwalWithTheme.error("Erro", message);
    }
  };

  const handleExcluirConcorrente = async (concorrente) => {
    const result = await SwalWithTheme.confirm("Excluir concorrente", `Deseja excluir o concorrente \"${concorrente.nome}\"?`, "Excluir", "Cancelar");
    if (!result.isConfirmed) return;

    try {
      await removerConcorrente(concorrente.id);
      setConcorrentesList(await listarConcorrentes());
      SwalWithTheme.success("Sucesso", "Concorrente removido");
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao remover concorrente";
      SwalWithTheme.error("Erro", message);
    }
  };

  const handleExcluirAviso = async (aviso) => {
    const result = await SwalWithTheme.confirm(
      "Excluir aviso",
      `Deseja excluir o aviso "${aviso.titulo}"? Ele sai do carrossel da tela inicial.`,
      "Excluir",
      "Cancelar"
    );
    if (!result.isConfirmed) return;

    try {
      await excluirAviso(aviso.id);
      setAvisosList(await listarAvisosParaGestao());
      SwalWithTheme.success("Sucesso", "Aviso removido");
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao remover aviso";
      SwalWithTheme.error("Erro", message);
    }
  };

  const handleExcluirWebhookRule = async (regra) => {
    const result = await SwalWithTheme.confirm(
      "Excluir automacao",
      `Deseja excluir a automacao do evento \"${regra.eventType}\"?`,
      "Excluir",
      "Cancelar"
    );
    if (!result.isConfirmed) return;

    try {
      await removerRegraWebhookJoin(regra.id);
      setJoinWebhookRules(await listarRegrasWebhookJoin());
      SwalWithTheme.success("Sucesso", "Automacao removida");
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao remover automacao do webhook";
      SwalWithTheme.error("Erro", message);
    }
  };

  const updatePolicyField = (eventCode, field, value) => {
    setNotificationPolicies((current) =>
      current.map((policy) =>
        policy.eventCode === eventCode
          ? { ...policy, [field]: value }
          : policy
      )
    );
  };

  const saveNotificationPolicy = async (policy) => {
    setSavingPolicyCode(policy.eventCode);
    try {
      const payload = {
        enabled: Boolean(policy.enabled),
        severity: policy.severity,
        notifyAssignee: Boolean(policy.notifyAssignee),
        notifyAdmins: Boolean(policy.notifyAdmins),
        notifyCreator: Boolean(policy.notifyCreator),
        notifySupervisors: Boolean(policy.notifySupervisors),
      };

      const updatedPolicy = await atualizarPoliticaNotificacao(policy.eventCode, payload);
      setNotificationPolicies((current) =>
        current.map((item) => (item.eventCode === updatedPolicy.eventCode ? updatedPolicy : item))
      );
      setSavedPolicies((current) => ({ ...current, [updatedPolicy.eventCode]: updatedPolicy }));
      SwalWithTheme.success("Sucesso", `Politica de ${policy.eventLabel} atualizada`);
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao salvar politica de notificacao";
      SwalWithTheme.error("Erro", message);
    } finally {
      setSavingPolicyCode(null);
    }
  };

  const motivosFieldsProgress = useMemo(
    () => hasVisibleField(normalizeMotivoFields(motivoForm.campos).fields),
    [motivoForm.campos]
  );

  const tiposColumns = [
    {
      key: "nomeTipo",
      header: "Tipo",
      mobilePrimary: true,
      mobileOrder: 0,
      render: (value, row) => {
        const iconDef = resolveTaskTypeIconDefinition(row);
        const Icon = iconDef.icon;
        return (
          <div className="flex min-w-0 items-center gap-3">
            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-border bg-muted/30 text-foreground">
              <Icon className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <p className="truncate font-medium text-foreground">{value || "Sem nome"}</p>
              <p className="truncate text-xs text-muted-foreground">{iconDef.label}</p>
            </div>
          </div>
        );
      },
    },
    { key: "scoreTipo", header: "Score", mobileSpan: 1, mobileOrder: 1 },
    {
      key: "regras",
      header: "Parceiros",
      mobileSpan: 2,
      mobileOrder: 2,
      render: (_, row) => describePartnerBinding(normalizeTipoRules(row.regras).partnerBindingMode),
    },
    {
      key: "checkPolicy",
      header: "Check",
      mobileSpan: 2,
      mobileOrder: 3,
      render: (_, row) => describeCheckPolicy(normalizeTipoRules(row.regras).checkPolicy),
    },
    {
      key: "fallback",
      header: "Fallback",
      mobileSpan: 2,
      mobileOrder: 4,
      render: (_, row) => describeStepPolicy(normalizeTipoRules(row.regras).fallback),
    },
    {
      key: "evidence",
      header: "Evidencia",
      mobileSpan: 2,
      mobileOrder: 5,
      render: (_, row) => describeStepPolicy(normalizeTipoRules(row.regras).evidence),
    },
  ];

  const motivosColumns = [
    { key: "nome", header: "Nome", mobilePrimary: true, mobileOrder: 0 },
    ...Object.values(TASK_FIELD_CODES).map((fieldCode, index) => ({
      key: fieldCode,
      header: FIELD_LABELS[fieldCode],
      mobileSpan: 1,
      mobileOrder: index + 1,
      render: (_, row) => describeFieldMode(normalizeMotivoFields(row.campos).fields[fieldCode]),
    })),
  ];

  const concorrentesColumns = [{ key: "nome", header: "Nome", mobilePrimary: true, mobileOrder: 0 }];

  const avisosColumns = [
    {
      key: "titulo",
      header: "Aviso",
      mobilePrimary: true,
      mobileOrder: 0,
      render: (value, row) => (
        <div className="flex min-w-0 items-center gap-3">
          {row.imagemUrl ? (
            <img
              src={row.imagemUrl}
              alt=""
              className="h-10 w-[76px] shrink-0 rounded-md border border-border object-cover"
            />
          ) : (
            <span className="grid h-10 w-[76px] shrink-0 place-items-center rounded-md border border-dashed border-border text-[10px] text-muted-foreground">
              sem arte
            </span>
          )}
          <div className="min-w-0">
            <p className="truncate font-medium text-foreground">{value}</p>
            <p className="truncate text-xs text-muted-foreground">{row.etiqueta || "Sem etiqueta"}</p>
          </div>
        </div>
      ),
    },
    {
      key: "somenteImagem",
      header: "Formato",
      mobileOrder: 1,
      mobileSpan: 1,
      render: (value) => (value ? "Somente imagem" : "Imagem com texto"),
    },
    { key: "ordem", header: "Ordem", mobileOrder: 2, mobileSpan: 1 },
    {
      key: "dataInicio",
      header: "Vigencia",
      mobileOrder: 3,
      render: (_, row) => formatVigencia(row),
    },
  ];

  const tableActionsAvisos = [
    { title: "Editar", icon: UserRoundPen, onClick: openAvisoModal },
    { title: "Excluir", icon: Trash2, onClick: handleExcluirAviso },
  ];

  const webhookRuleColumns = [
    {
      key: "eventType",
      header: "Evento",
      mobilePrimary: true,
      mobileOrder: 0,
      render: (value, row) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-foreground">{value || "Sem eventType"}</p>
          <p className="truncate text-xs text-muted-foreground">{row.descricao || "Sem descricao adicional"}</p>
        </div>
      ),
    },
    {
      key: "tipoTarefaNome",
      header: "Acao",
      mobileOrder: 1,
      render: (_, row) => {
        if (!row.criarTarefa) {
          return row.gerarNotificacao ? "Somente notificacao" : "Sem automacao";
        }
        return row.tipoTarefaNome || "Criar tarefa";
      },
    },
    {
      key: "usuarioResponsavelNome",
      header: "Responsavel",
      mobileOrder: 2,
      render: (value, row) => value || (row.criarTarefa ? "Responsável do parceiro" : "Não se aplica"),
    },
    {
      key: "gerarNotificacao",
      header: "Notificacao",
      mobileOrder: 3,
      render: (value) => (value ? "Sim" : "Não"),
    },
    {
      key: "ativo",
      header: "Status",
      mobileOrder: 4,
      render: (value) => <StatusBadge status={value ? "Ativa" : "Pausada"} variant={value ? "success" : "default"} />,
    },
  ];

  const tiposOptions = useMemo(
    () => tiposList.map((tipo) => ({ value: tipo.id, label: tipo.nomeTipo || `Tipo #${tipo.id}` })),
    [tiposList]
  );

  const motivosOptions = useMemo(
    () => motivosList.map((motivo) => ({ value: motivo.id, label: motivo.nome || `Motivo #${motivo.id}` })),
    [motivosList]
  );

  const usuariosOptions = useMemo(
    () => usuariosList.map((usuario) => ({ value: usuario.id, label: usuario.nome || `Usuario #${usuario.id}` })),
    [usuariosList]
  );

  const tableActionsTipos = [
    { icon: UserRoundPen, title: "Editar", onClick: openTipoModal },
    { icon: Trash2, title: "Excluir", onClick: handleExcluirTipo },
  ];

  const tableActionsMotivos = [
    { icon: UserRoundPen, title: "Editar", onClick: openMotivoModal },
    { icon: Trash2, title: "Excluir", onClick: handleExcluirMotivo },
  ];

  const tableActionsConcorrentes = [
    { icon: UserRoundPen, title: "Editar", onClick: openConcorrenteModal },
    { icon: Trash2, title: "Excluir", onClick: handleExcluirConcorrente },
  ];

  const tableActionsWebhookRules = [
    { icon: UserRoundPen, title: "Editar", onClick: openWebhookRuleModal },
    { icon: Trash2, title: "Excluir", onClick: handleExcluirWebhookRule },
  ];

  const tipoProgress = calcProgress([
    Boolean(String(tipoForm.nomeTipo || "").trim()),
    tipoForm.scoreTipo !== "" && tipoForm.scoreTipo !== null && tipoForm.scoreTipo !== undefined,
    Boolean(normalizeTipoRules(tipoForm.regras).partnerBindingMode),
    Boolean(normalizeTipoRules(tipoForm.regras).checkPolicy),
  ]);

  const tipoIconDefinition = useMemo(
    () => resolveTaskTypeIconDefinition({ icone: tipoForm.icone, nomeTipo: tipoForm.nomeTipo }),
    [tipoForm.icone, tipoForm.nomeTipo]
  );
  const TipoIconPreview = tipoIconDefinition.icon;

  const motivoProgress = calcProgress([
    Boolean(String(motivoForm.nome || "").trim()),
    motivosFieldsProgress,
  ]);

  const concorrenteProgress = calcProgress([Boolean(String(concorrenteForm.nome || "").trim())]);

  const avisoProgress = calcProgress([
    Boolean(String(avisoForm.titulo || "").trim()),
    Boolean(avisoImagemFile || String(avisoForm.imagemUrl || "").trim()),
    avisoForm.somenteImagem ? true : Boolean(String(avisoForm.descricao || "").trim()),
  ]);

  const webhookRuleProgress = calcProgress([
    Boolean(String(webhookRuleForm.eventType || "").trim()),
    webhookRuleForm.criarTarefa ? Boolean(webhookRuleForm.tipoTarefaId) : true,
    webhookRuleForm.criarTarefa || webhookRuleForm.gerarNotificacao,
    webhookRuleForm.gerarNotificacao
      ? Boolean(
        String(webhookRuleForm.tituloNotificacaoTemplate || "").trim() ||
        String(webhookRuleForm.mensagemNotificacaoTemplate || "").trim()
      )
      : true,
  ]);

  const isPolicyDirty = (policy) => {
    const saved = savedPolicies[policy.eventCode];
    if (!saved) return false;
    return POLICY_EDITABLE_FIELDS.some((field) =>
      field === "severity" ? (policy.severity || "INFO") !== (saved.severity || "INFO") : Boolean(policy[field]) !== Boolean(saved[field])
    );
  };

  const contagemPorSecao = {
    tarefas: tiposList.length + motivosList.length + concorrentesList.length,
    join: joinWebhookRules.length,
    notificacoes: notificationPolicies.filter((policy) => policy.enabled).length,
    hub: avisosList.length,
  };

  const secoesTabs = SECOES.map((secao) => ({
    ...secao,
    label: contagemPorSecao[secao.value] === undefined ? secao.label : (
      <>
        {secao.label}
        <span className="rounded-full bg-background px-1.5 py-px text-[10px] font-semibold tabular-nums text-muted-foreground">
          {contagemPorSecao[secao.value]}
        </span>
      </>
    ),
  }));

  if (loading) {
    return <PageLoader label="Carregando configurações..." />;
  }

  if (!isAdmin) {
    return (
      <Card>
        <div className="py-10 text-center text-sm text-muted-foreground">
          Acesso restrito: apenas administradores podem acessar esta tela.
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <header className="page-header">
        <h1 className="page-title">Configurações</h1>
        <p className="page-subtitle">Regras de tarefas, automações da Join, notificações, avisos do hub e régua de churn.</p>
      </header>

      <Tabs
        items={secoesTabs}
        value={abaAtiva}
        onChange={setAbaAtiva}
        idPrefix="config-abas"
        ariaLabel="Seções de configuração"
      />

      <div {...tabPanelProps("config-abas", abaAtiva)} className="space-y-4">
        {abaAtiva === "tarefas" && (
          <>
            <SecaoConfig
              icon={Shapes}
              title="Tipos de tarefa"
              description="Vínculo com parceiro, check, fallback e evidência saem de uma política única por tipo."
              action={
                <Button size="sm" onClick={() => openTipoModal()}>
                  <PlusCircle className="h-4 w-4" />
                  Novo tipo
                </Button>
              }
            >
              <DataTable columns={tiposColumns} data={tiposList} actions={tableActionsTipos} enablePagination={false} />
            </SecaoConfig>

            <SecaoConfig
              icon={MessageSquare}
              title="Motivos da tarefa"
              description="Cada campo da conclusão tem modo próprio: oculto, opcional ou obrigatório."
              action={
                <Button size="sm" onClick={() => openMotivoModal()}>
                  <PlusCircle className="h-4 w-4" />
                  Novo motivo
                </Button>
              }
            >
              <DataTable columns={motivosColumns} data={motivosList} actions={tableActionsMotivos} enablePagination={false} />
            </SecaoConfig>

            <SecaoConfig
              icon={Users2}
              title="Concorrentes"
              description="Lista usada na conclusão de tarefa para mapear produção por concorrente."
              action={
                <Button size="sm" onClick={() => openConcorrenteModal()}>
                  <PlusCircle className="h-4 w-4" />
                  Novo concorrente
                </Button>
              }
            >
              <DataTable columns={concorrentesColumns} data={concorrentesList} actions={tableActionsConcorrentes} enablePagination={false} />
            </SecaoConfig>
          </>
        )}

        {abaAtiva === "join" && (
          <SecaoConfig
            icon={Webhook}
            title="Automação do webhook Join"
            description="Cada eventType da Join pode atualizar o parceiro, criar tarefa vinculada e ainda gerar notificação extra."
            action={
              <Button size="sm" onClick={() => openWebhookRuleModal()}>
                <PlusCircle className="h-4 w-4" />
                Nova automação
              </Button>
            }
          >
            <DataTable columns={webhookRuleColumns} data={joinWebhookRules} actions={tableActionsWebhookRules} enablePagination={false} />
          </SecaoConfig>
        )}

        {abaAtiva === "notificacoes" && (
          <SecaoConfig
            icon={BellRing}
            title="Notificações e alertas"
            description="Cada evento entra na central de notificações, é enviado em tempo real por WebSocket e vira Web Push nos dispositivos inscritos. Aqui você escolhe quem recebe e com qual severidade."
          >
            {notificationPolicies.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
                Nenhuma política de notificação encontrada.
              </div>
            ) : (
              <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border">
                {notificationPolicies.map((policy) => {
                  const dirty = isPolicyDirty(policy);
                  const saving = savingPolicyCode === policy.eventCode;
                  return (
                    <li key={policy.eventCode} className="space-y-3 bg-card p-4">
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                        <label className="flex min-w-0 items-start gap-3">
                          <Checkbox
                            className="mt-0.5"
                            checked={Boolean(policy.enabled)}
                            onCheckedChange={(checked) => updatePolicyField(policy.eventCode, "enabled", Boolean(checked))}
                            aria-label={`Evento ${policy.eventLabel} ativo`}
                          />
                          <span className="min-w-0 space-y-1">
                            <span className="flex flex-wrap items-center gap-2">
                              <span className="text-sm font-semibold text-foreground">{policy.eventLabel}</span>
                              <StatusBadge status={policy.enabled ? "Ativo" : "Pausado"} variant={policy.enabled ? "success" : "default"} />
                            </span>
                            <span className="block text-xs text-muted-foreground">{policy.description}</span>
                          </span>
                        </label>

                        <div className="flex shrink-0 items-center gap-2 lg:w-[300px]">
                          <div className="min-w-0 flex-1">
                            <FormField
                              type="select"
                              value={policy.severity || "INFO"}
                              options={NOTIFICATION_SEVERITY_OPTIONS}
                              disabled={!policy.enabled}
                              aria-label="Severidade"
                              onChange={(e) => updatePolicyField(policy.eventCode, "severity", e.target.value)}
                            />
                          </div>
                          <Button
                            size="sm"
                            variant={dirty ? "primary" : "outline"}
                            onClick={() => saveNotificationPolicy(policy)}
                            disabled={!dirty || saving}
                          >
                            {saving ? "Salvando..." : dirty ? "Salvar" : "Salvo"}
                          </Button>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2 pl-7">
                        <span className="text-xs text-muted-foreground">Enviar para:</span>
                        {POLICY_RECIPIENTS.map((recipient) => (
                          <RecipientToggle
                            key={recipient.field}
                            label={recipient.label}
                            description={recipient.description}
                            pressed={Boolean(policy[recipient.field])}
                            disabled={!policy.enabled}
                            onToggle={() => updatePolicyField(policy.eventCode, recipient.field, !policy[recipient.field])}
                          />
                        ))}
                        {dirty && !saving ? (
                          <span className="text-xs font-medium text-warning">Alterações não salvas</span>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </SecaoConfig>
        )}

        {abaAtiva === "hub" && (
          <SecaoConfig
            icon={Megaphone}
            title="Avisos do hub"
            description="Carrossel da tela inicial. A arte aparece em 3:1 e preenche o cartão (o excesso é cortado): use algo largo, por volta de 1600×520."
            action={
              <Button size="sm" onClick={() => openAvisoModal()}>
                <PlusCircle className="h-4 w-4" />
                Novo aviso
              </Button>
            }
          >
            <DataTable columns={avisosColumns} data={avisosList} actions={tableActionsAvisos} enablePagination={false} />
          </SecaoConfig>
        )}

        {abaAtiva === "pre-filtros" && <PreFiltrosSection />}

        {abaAtiva === "campanhas" && <CampanhasSection />}

        {abaAtiva === "churn" && (
          <SecaoConfig
            icon={Gauge}
            title="Régua de churn"
            description="Percentual entregue no ciclo atual em relação ao volume esperado pela rotina de cada parceiro."
            action={
              <Button size="sm" onClick={salvarReguaChurn} disabled={savingChurnRegua}>
                {savingChurnRegua ? "Salvando..." : "Salvar régua"}
              </Button>
            }
          >
            <div className="grid gap-4 md:grid-cols-3">
              <FormField
                label="Atenção abaixo de (%)"
                type="number"
                min="0"
                max="100"
                step="1"
                value={churnRegua.entregaAtencao}
                onChange={(event) => setChurnRegua((current) => ({ ...current, entregaAtencao: event.target.value }))}
              />
              <FormField
                label="Risco abaixo de (%)"
                type="number"
                min="0"
                max="100"
                step="1"
                value={churnRegua.entregaRisco}
                onChange={(event) => setChurnRegua((current) => ({ ...current, entregaRisco: event.target.value }))}
              />
              <FormField
                label="Crítico abaixo de (%)"
                type="number"
                min="0"
                max="100"
                step="1"
                value={churnRegua.entregaCritico}
                onChange={(event) => setChurnRegua((current) => ({ ...current, entregaCritico: event.target.value }))}
              />
            </div>

            <ReguaChurnPreview regua={churnRegua} />

            <p className="mt-3 text-xs text-muted-foreground">
              Saudável fica acima do primeiro limite. A frequência continua sendo calculada pela rotina individual; entre volume e frequência, prevalece o pior sinal.
            </p>
          </SecaoConfig>
        )}
      </div>

      <Modal
        isOpen={showTipoModal}
        onClose={() => setShowTipoModal(false)}
        onCancel={() => setShowTipoModal(false)}
        onSave={saveTipo}
        title={editingTipoId ? "Editar tipo" : "Novo tipo"}
        maxWidth="max-w-3xl"
        progress={tipoProgress}
        progressLabel="Preenchimento do tipo"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <FormField
              label="Nome do tipo"
              value={tipoForm.nomeTipo}
              onChange={(e) => setTipoForm((prev) => ({ ...prev, nomeTipo: e.target.value }))}
              required
            />
            <FormField
              label="Icone"
              type="select"
              value={tipoForm.icone}
              placeholder="Usar sugestao automatica"
              options={TASK_TYPE_ICON_OPTIONS}
              onChange={(e) => setTipoForm((prev) => ({ ...prev, icone: e.target.value }))}
            />
            <FormField
              label="Score"
              type="number"
              value={tipoForm.scoreTipo}
              onChange={(e) => setTipoForm((prev) => ({ ...prev, scoreTipo: e.target.value }))}
            />
          </div>

          <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/20 p-3">
            <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-border bg-background/80 text-foreground">
              <TipoIconPreview className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">Preview do tipo</p>
              <p className="text-xs text-muted-foreground">
                {tipoForm.icone
                  ? `Icone escolhido: ${tipoIconDefinition.label}`
                  : `Sem icone fixo. O sistema usara a melhor sugestao para "${tipoForm.nomeTipo || "este tipo"}".`}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField
              label="Regra de vinculo com parceiro"
              type="select"
              value={normalizeTipoRules(tipoForm.regras).partnerBindingMode}
              options={PARTNER_BINDING_OPTIONS}
              onChange={(e) => updateTipoRules((rules) => ({ ...rules, partnerBindingMode: e.target.value }))}
            />
            <FormField
              label="Politica de check"
              type="select"
              value={normalizeTipoRules(tipoForm.regras).checkPolicy}
              options={CHECK_POLICY_OPTIONS}
              onChange={(e) => updateTipoRules((rules) => ({ ...rules, checkPolicy: e.target.value }))}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="space-y-3 rounded-lg border border-border p-3">
              <h3 className="text-sm font-semibold text-foreground">Fallback</h3>
              <FormField
                label="Modo"
                type="select"
                value={normalizeTipoRules(tipoForm.regras).fallback.mode}
                options={STEP_MODE_OPTIONS}
                onChange={(e) => updateTipoRules((rules) => ({
                  ...rules,
                  fallback: { ...rules.fallback, mode: e.target.value },
                }))}
              />
              <FormField
                label="Imagem"
                type="select"
                value={normalizeTipoRules(tipoForm.regras).fallback.imageRequirement}
                options={IMAGE_REQUIREMENT_OPTIONS}
                disabled={normalizeTipoRules(tipoForm.regras).fallback.mode !== "ENABLED"}
                onChange={(e) => updateTipoRules((rules) => ({
                  ...rules,
                  fallback: { ...rules.fallback, imageRequirement: e.target.value },
                }))}
              />
              <FormField
                label="Aprovação"
                type="select"
                value={normalizeTipoRules(tipoForm.regras).fallback.approvalMode}
                options={APPROVAL_MODE_OPTIONS}
                disabled={normalizeTipoRules(tipoForm.regras).fallback.mode !== "ENABLED"}
                onChange={(e) => updateTipoRules((rules) => ({
                  ...rules,
                  fallback: { ...rules.fallback, approvalMode: e.target.value },
                }))}
              />
            </div>

            <div className="space-y-3 rounded-lg border border-border p-3">
              <h3 className="text-sm font-semibold text-foreground">Evidencia</h3>
              <FormField
                label="Modo"
                type="select"
                value={normalizeTipoRules(tipoForm.regras).evidence.mode}
                options={STEP_MODE_OPTIONS}
                onChange={(e) => updateTipoRules((rules) => ({
                  ...rules,
                  evidence: { ...rules.evidence, mode: e.target.value },
                }))}
              />
              <FormField
                label="Imagem"
                type="select"
                value={normalizeTipoRules(tipoForm.regras).evidence.imageRequirement}
                options={IMAGE_REQUIREMENT_OPTIONS}
                disabled={normalizeTipoRules(tipoForm.regras).evidence.mode !== "ENABLED"}
                onChange={(e) => updateTipoRules((rules) => ({
                  ...rules,
                  evidence: { ...rules.evidence, imageRequirement: e.target.value },
                }))}
              />
              <FormField
                label="Aprovação"
                type="select"
                value={normalizeTipoRules(tipoForm.regras).evidence.approvalMode}
                options={APPROVAL_MODE_OPTIONS}
                disabled={normalizeTipoRules(tipoForm.regras).evidence.mode !== "ENABLED"}
                onChange={(e) => updateTipoRules((rules) => ({
                  ...rules,
                  evidence: { ...rules.evidence, approvalMode: e.target.value },
                }))}
              />
            </div>
          </div>

          <div className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
            <CheckCircle2 className="mr-1 inline h-3.5 w-3.5" />
            O backend valida coerencia entre essas politicas. Tipos com check por geolocalizacao exigem parceiro unico.
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showMotivoModal}
        onClose={() => setShowMotivoModal(false)}
        onCancel={() => setShowMotivoModal(false)}
        onSave={saveMotivo}
        title={editingMotivoId ? "Editar motivo" : "Novo motivo"}
        maxWidth="max-w-2xl"
        progress={motivoProgress}
        progressLabel="Preenchimento do motivo"
      >
        <div className="space-y-4">
          <FormField
            label="Nome do motivo"
            value={motivoForm.nome}
            onChange={(e) => setMotivoForm((prev) => ({ ...prev, nome: e.target.value }))}
            required
          />

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {Object.values(TASK_FIELD_CODES).map((fieldCode) => (
              <FormField
                key={fieldCode}
                label={`Campo ${FIELD_LABELS[fieldCode]}`}
                type="select"
                value={normalizeMotivoFields(motivoForm.campos).fields[fieldCode]}
                options={FIELD_MODE_OPTIONS}
                onChange={(e) => updateMotivoField(fieldCode, e.target.value)}
              />
            ))}
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showConcorrenteModal}
        onClose={() => setShowConcorrenteModal(false)}
        onCancel={() => setShowConcorrenteModal(false)}
        onSave={saveConcorrente}
        title={editingConcorrenteId ? "Editar concorrente" : "Novo concorrente"}
        progress={concorrenteProgress}
        progressLabel="Preenchimento do concorrente"
      >
        <div className="space-y-3">
          <FormField
            label="Nome do concorrente"
            value={concorrenteForm.nome}
            onChange={(e) => setConcorrenteForm((prev) => ({ ...prev, nome: e.target.value }))}
            required
          />
        </div>
      </Modal>

      <Modal
        isOpen={showAvisoModal}
        onClose={() => setShowAvisoModal(false)}
        onCancel={() => setShowAvisoModal(false)}
        onSave={saveAviso}
        title={editingAvisoId ? "Editar aviso do hub" : "Novo aviso do hub"}
        maxWidth="max-w-3xl"
        progress={avisoProgress}
        progressLabel="Preenchimento do aviso"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField
              label="Titulo"
              value={avisoForm.titulo}
              onChange={(e) => setAvisoForm((prev) => ({ ...prev, titulo: e.target.value }))}
              placeholder="Ex.: Nova campanha de consignado"
              required
            />
            <FormField
              label="Etiqueta"
              value={avisoForm.etiqueta}
              onChange={(e) => setAvisoForm((prev) => ({ ...prev, etiqueta: e.target.value }))}
              placeholder="Ex.: NOVIDADE"
            />
          </div>

          <div className="space-y-1.5">
            <FormField
              label="Descrição"
              type="textarea"
              value={avisoForm.descricao}
              onChange={(e) => setAvisoForm((prev) => ({ ...prev, descricao: e.target.value }))}
              placeholder="Uma ou duas frases. Use **texto** para negrito e Enter para quebrar linha."
            />
            {avisoForm.descricao ? (
              <div className="rounded-lg border border-border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
                <span className="mb-1 block font-medium text-foreground">Como vai aparecer</span>
                <TextoRico texto={avisoForm.descricao} />
              </div>
            ) : null}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <FormField
              label="Link de destino"
              value={avisoForm.linkDestino}
              onChange={(e) => setAvisoForm((prev) => ({ ...prev, linkDestino: e.target.value }))}
              placeholder="/parceiros"
            />
            <FormField
              label="Texto do botao"
              value={avisoForm.acaoLabel}
              onChange={(e) => setAvisoForm((prev) => ({ ...prev, acaoLabel: e.target.value }))}
              placeholder="Ex.: Ver parceiros"
            />
            <FormField
              label="Ordem"
              type="number"
              value={avisoForm.ordem}
              onChange={(e) => setAvisoForm((prev) => ({ ...prev, ordem: e.target.value }))}
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField
              label="Exibir a partir de"
              type="date"
              value={avisoForm.dataInicio}
              onChange={(e) => setAvisoForm((prev) => ({ ...prev, dataInicio: e.target.value }))}
            />
            <FormField
              label="Exibir ate"
              type="date"
              value={avisoForm.dataFim}
              onChange={(e) => setAvisoForm((prev) => ({ ...prev, dataFim: e.target.value }))}
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-foreground">
            <Checkbox
              checked={avisoForm.somenteImagem}
              onCheckedChange={(checked) => setAvisoForm((prev) => ({ ...prev, somenteImagem: Boolean(checked) }))}
            />
            <span>Somente imagem (banner pronto, sem titulo nem botao por cima)</span>
          </label>

          <div className="space-y-2 rounded-xl border border-border bg-muted/20 p-3">
            <p className="text-sm font-medium text-foreground">Imagem do aviso</p>
            <p className="text-xs text-muted-foreground">
              O cartao usa proporcao 3:1 e a imagem preenche, cortando o excesso nas bordas. Ideal por volta de
              1600x520. PNG, JPG, WEBP ou SVG, ate 3 MB.
            </p>

            {avisoForm.imagemUrl && !avisoImagemFile ? (
              <img
                src={avisoForm.imagemUrl}
                alt=""
                className="aspect-[3/1] w-full max-w-md rounded-lg border border-border object-cover"
              />
            ) : null}

            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              onChange={(event) => setAvisoImagemFile(event.target.files?.[0] || null)}
              className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border-0 file:bg-action file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-action-foreground"
            />
            {avisoImagemFile ? (
              <p className="text-xs text-muted-foreground">
                {avisoImagemFile.name} sera enviada ao salvar.
              </p>
            ) : null}
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showWebhookRuleModal}
        onClose={() => setShowWebhookRuleModal(false)}
        onCancel={() => setShowWebhookRuleModal(false)}
        onSave={saveWebhookRule}
        title={editingWebhookRuleId ? "Editar automacao Join" : "Nova automacao Join"}
        maxWidth="max-w-4xl"
        progress={webhookRuleProgress}
        progressLabel="Preenchimento da automacao"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <FormField
              label="eventType"
              value={webhookRuleForm.eventType}
              onChange={(e) => setWebhookRuleForm((prev) => ({ ...prev, eventType: e.target.value }))}
              placeholder="partner.updated"
              required
            />
            <FormField
              label="Prioridade fixa"
              type="select"
              value={webhookRuleForm.prioridade}
              options={WEBHOOK_TASK_PRIORITY_OPTIONS}
              placeholder="Sem prioridade fixa"
              disabled={!webhookRuleForm.criarTarefa}
              onChange={(e) => setWebhookRuleForm((prev) => ({ ...prev, prioridade: e.target.value }))}
            />
            <FormField
              label="Responsavel fixo"
              type="select"
              value={webhookRuleForm.usuarioResponsavelId}
              options={usuariosOptions}
              placeholder="Usar responsavel do parceiro"
              disabled={!webhookRuleForm.criarTarefa}
              onChange={(e) => setWebhookRuleForm((prev) => ({ ...prev, usuarioResponsavelId: e.target.value }))}
            />
          </div>

          <FormField
            label="Descricao"
            type="textarea"
            value={webhookRuleForm.descricao}
            onChange={(e) => setWebhookRuleForm((prev) => ({ ...prev, descricao: e.target.value }))}
            placeholder="Explique rapidamente o que esta automacao faz"
          />

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
            <CheckboxField
              label="Regra ativa"
              description="Quando pausada, o webhook continua atualizando parceiro, mas nao aplica esta automacao."
              checked={Boolean(webhookRuleForm.ativo)}
              onChange={(checked) => setWebhookRuleForm((prev) => ({ ...prev, ativo: checked }))}
            />
            <CheckboxField
              label="Criar tarefa"
              description="Gera uma tarefa vinculada ao parceiro assim que o evento for recebido."
              checked={Boolean(webhookRuleForm.criarTarefa)}
              onChange={(checked) => setWebhookRuleForm((prev) => ({ ...prev, criarTarefa: checked }))}
            />
            <CheckboxField
              label="Gerar notificacao"
              description="Publica uma notificacao adicional usando a politica do evento Join."
              checked={Boolean(webhookRuleForm.gerarNotificacao)}
              onChange={(checked) => setWebhookRuleForm((prev) => ({ ...prev, gerarNotificacao: checked }))}
            />
          </div>

          {webhookRuleForm.criarTarefa && (
            <div className="space-y-4 rounded-xl border border-border bg-card/70 p-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <FormField
                  label="Tipo de tarefa"
                  type="select"
                  value={webhookRuleForm.tipoTarefaId}
                  options={tiposOptions}
                  onChange={(e) => setWebhookRuleForm((prev) => ({ ...prev, tipoTarefaId: e.target.value }))}
                  required
                />
                <MultiSelectField
                  label="Motivos"
                  options={motivosOptions}
                  selectedValues={webhookRuleForm.motivosIds}
                  onChange={(values) => setWebhookRuleForm((prev) => ({ ...prev, motivosIds: values }))}
                  placeholder="Selecionar motivos opcionais"
                />
              </div>

              <FormField
                label="Titulo da tarefa"
                type="textarea"
                value={webhookRuleForm.tituloTarefaTemplate}
                onChange={(e) => setWebhookRuleForm((prev) => ({ ...prev, tituloTarefaTemplate: e.target.value }))}
                placeholder="Se vazio, o sistema monta um titulo padrao"
              />
              <FormField
                label="Observacoes da tarefa"
                type="textarea"
                value={webhookRuleForm.observacoesTarefaTemplate}
                onChange={(e) => setWebhookRuleForm((prev) => ({ ...prev, observacoesTarefaTemplate: e.target.value }))}
                placeholder="Detalhes livres do evento que vao para a tarefa"
              />
            </div>
          )}

          {webhookRuleForm.gerarNotificacao && (
            <div className="space-y-4 rounded-xl border border-border bg-card/70 p-4">
              <FormField
                label="Titulo da notificacao"
                type="textarea"
                value={webhookRuleForm.tituloNotificacaoTemplate}
                onChange={(e) => setWebhookRuleForm((prev) => ({ ...prev, tituloNotificacaoTemplate: e.target.value }))}
                placeholder="Se vazio, o sistema monta um titulo padrao"
              />
              <FormField
                label="Mensagem da notificacao"
                type="textarea"
                value={webhookRuleForm.mensagemNotificacaoTemplate}
                onChange={(e) => setWebhookRuleForm((prev) => ({ ...prev, mensagemNotificacaoTemplate: e.target.value }))}
                placeholder="Conteudo adicional para a central de notificacoes"
              />
            </div>
          )}

          <div className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
            <Webhook className="mr-1 inline h-3.5 w-3.5" />
            Tokens disponiveis nos templates: {"{{eventType}}"}, {"{{eventId}}"}, {"{{parceiro.nome}}"}, {"{{parceiro.idJoin}}"},
            {" {{responsavel.nome}}"}, {"{{tarefa.id}}"}, {"{{tarefa.tipo}}"} e qualquer campo bruto recebido como {"{{payload.algumCampo}}"}.
          </div>
        </div>
      </Modal>
    </div>
  );
}

// Cabeçalho único para todas as seções: ícone, título, explicação e ação principal no mesmo lugar.
function SecaoConfig({ icon: Icon, title, description, action, children }) {
  return (
    <Card>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-muted text-primary">
            <Icon className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0 space-y-1">
            <h2 className="font-display text-lg font-semibold text-foreground">{title}</h2>
            {description ? <p className="max-w-3xl text-sm text-muted-foreground">{description}</p> : null}
          </div>
        </div>
        {action ? <div className="flex shrink-0 flex-wrap items-center gap-2">{action}</div> : null}
      </div>
      {children}
    </Card>
  );
}

function RecipientToggle({ label, description, pressed, disabled, onToggle }) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      title={description}
      disabled={disabled}
      onClick={onToggle}
      className={
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors " +
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 " +
        (pressed
          ? "border-primary/40 bg-primary/10 text-foreground"
          : "border-border text-muted-foreground hover:bg-accent hover:text-foreground")
      }
    >
      {pressed ? <Check className="h-3.5 w-3.5 text-primary" aria-hidden="true" /> : null}
      {label}
    </button>
  );
}

// Mostra as faixas da régua como ficam no relatório de churn (mesmas cores de StatusBadge).
function ReguaChurnPreview({ regua }) {
  const atencao = Number(regua.entregaAtencao);
  const risco = Number(regua.entregaRisco);
  const critico = Number(regua.entregaCritico);
  const brutos = [regua.entregaAtencao, regua.entregaRisco, regua.entregaCritico];
  const valida =
    brutos.every((bruto) => bruto !== "" && bruto !== null && Number.isFinite(Number(bruto)) && Number(bruto) >= 0 && Number(bruto) <= 100) &&
    atencao > risco &&
    risco > critico;

  if (!valida) {
    return (
      <p className="mt-4 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning">
        Preencha limites entre 0 e 100, em ordem decrescente (atenção &gt; risco &gt; crítico), para ver a prévia da régua.
      </p>
    );
  }

  const faixas = [
    { label: "Crítico", de: 0, ate: critico, className: "bg-badge-critical" },
    { label: "Risco", de: critico, ate: risco, className: "bg-badge-warning" },
    { label: "Atenção", de: risco, ate: atencao, className: "bg-badge-attention" },
    { label: "Saudável", de: atencao, ate: 100, className: "bg-badge-success" },
  ];

  return (
    <div className="mt-4 space-y-2" aria-label="Prévia da régua de churn">
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-muted">
        {faixas.map((faixa) => (
          <div key={faixa.label} className={faixa.className} style={{ width: `${faixa.ate - faixa.de}%` }} />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {faixas.map((faixa) => (
          <span key={faixa.label} className="inline-flex items-center gap-1.5">
            <span className={`h-2 w-2 rounded-full ${faixa.className}`} aria-hidden="true" />
            <span className="font-medium text-foreground">{faixa.label}</span>
            {faixa.de}%–{faixa.ate}%
          </span>
        ))}
      </div>
    </div>
  );
}

function CheckboxField({ label, description, checked, onChange }) {
  return (
    <div className="rounded-xl border border-border bg-card/70 p-3">
      <label className="flex items-center gap-3 text-sm font-medium text-foreground">
        <Checkbox checked={checked} onCheckedChange={onChange} />
        {label}
      </label>
      <p className="mt-2 text-xs text-muted-foreground">{description}</p>
    </div>
  );
}
